/**
 * useDamScope — ponto único ("dam scope") do modelo multibarragem — GUPM-156.
 *
 * Resolve, num só lugar:
 *   1. QUAIS barragens o usuário vê (hoje: todas as ativas; futuro: recorte por
 *      ACL de perfil — slot em `filtrarBarragensPorAcl`, sem tocar nas telas);
 *   2. o STATUS consolidado A/B/C (semáforo geral + breakdown por barragem).
 *
 * Header (GUPM-158) e Dashboard (GUPM-160) consomem daqui — nenhuma tela lê
 * barragem/status direto do banco. Regra #2: UI nunca chama Supabase direto.
 *
 * Status por barragem: hoje calculado do único sinal automático real —
 * instrumentos com leitura atual ≠ NORMAL (`readings_current`). Os demais
 * encaixes (perda de sinal, TARP, declaração ANM) entram sem mudar este hook
 * (Opção A do GUPM-157).
 */
import { useQuery } from '@tanstack/react-query';
import { pollingQueryOptions } from '@/lib/query-polling';
import { useDataSources } from '@/services/datasources';
import {
  computeStatusEstrutura,
  consolidarBarragens,
  contarEmNivelPorBarragem,
  filtrarBarragensPorAcl,
  type BarragemStatus,
  type StatusEstrutura,
} from '@/domain/estrutura';
import {
  contarTarpsPorBarragem,
  declaracaoAnmAtivaPorBarragem,
  OPEN_STATUSES,
} from '@/domain/tarp';
import { useDams } from './useDams';

export interface DamScope {
  /** Barragens visíveis (ativas + recorte de ACL), cada uma com status calculado. */
  dams: BarragemStatus[];
  /** Pior status entre as barragens visíveis (NORMAL se vazio). */
  consolidado: StatusEstrutura;
  isLoading: boolean;
  error: Error | null;
}

export function useDamScope(): DamScope {
  const { data: damsAtivas = [], isLoading: loadingDams, error: errDams } = useDams();
  const { instrumentacao, tarp } = useDataSources();

  // Mesma queryKey do useM1Data → TanStack Query compartilha o fetch (sem duplicar).
  const {
    data: current = [],
    isLoading: loadingRead,
    error: errRead,
  } = useQuery({
    queryKey: ['readings', 'current'],
    queryFn: () => instrumentacao.listCurrentReadings(),
    ...pollingQueryOptions,
  });

  // GUPM-307 — o cadastro dos instrumentos, para excluir os declarados fora de serviço da
  // contagem. Mesma queryKey do `useM1Data` (`['instruments']`), então o TanStack Query
  // compartilha o fetch e isto não acrescenta requisição nenhuma.
  const {
    data: instruments = [],
    isLoading: loadingInst,
    error: errInst,
  } = useQuery({
    queryKey: ['instruments'],
    queryFn: () => instrumentacao.listInstruments(),
    ...pollingQueryOptions,
  });

  // Encaixe TARP (GUPM-172): TARP não baixada → estrutura em Observação (pré-ANM).
  // queryKey compartilhada com o M3 (useOpenTarps) — sem fetch duplicado.
  const {
    data: openTarps = [],
    isLoading: loadingTarps,
    error: errTarps,
  } = useQuery({
    queryKey: ['tarps', 'open'],
    queryFn: () => tarp.listTarps({ status: [...OPEN_STATUSES] }),
    ...pollingQueryOptions,
  });

  // Encaixe TARP baixa (GUPM-182): a declaração ANM da baixa mais recente promove
  // o semáforo da barragem (Res. 220 — única via de promoção a nível ANM). Só a UI
  // do semáforo muda; a fonte é `tarp_records` ENCERRADA (leitura já existente).
  const {
    data: encerradas = [],
    isLoading: loadingEncerradas,
    error: errEncerradas,
  } = useQuery({
    queryKey: ['tarps', 'encerradas'],
    queryFn: () => tarp.listBaixasEncerradas(),
    ...pollingQueryOptions,
  });

  // Slot de ACL — hoje passthrough (todos veem todas as ativas).
  const visiveis = filtrarBarragensPorAcl(damsAtivas);
  // GUPM-307 — os instrumentos entram aqui para excluir os declarados fora de servico.
  // Sem eles, a leitura congelada de um instrumento retirado de campo conta como
  // "instrumento em nivel" e acende o chip da barragem no header, em TODA tela.
  const emNivel = contarEmNivelPorBarragem(current, instruments);
  const tarpCounts = contarTarpsPorBarragem(openTarps);
  const declaracoes = declaracaoAnmAtivaPorBarragem(encerradas);
  // GUPM-291: a ABERTURA não declara mais nível. Uma TARP de emergência aberta
  // conta como TARP não baixada (→ OBSERVACAO, pré-ANM) como qualquer outra; só o
  // enquadramento da BAIXA (`declaracoes`) promove a nível ANM. Reverte a
  // promoção "na hora" do GUPM-207: declarar nível de barragem é ato do RT, não
  // efeito de um acionamento do vigilante.

  const dams: BarragemStatus[] = visiveis.map((d) => ({
    id: d.id,
    code: d.code,
    name: d.name,
    status: computeStatusEstrutura({
      instrumentosEmNivel: emNivel[d.id] ?? 0,
      tarpsAbertas: tarpCounts[d.id]?.abertas ?? 0,
      tarpsEmAnaliseRt: tarpCounts[d.id]?.emAnaliseRt ?? 0,
      declaracaoAnm: declaracoes[d.id] ?? null,
    }),
  }));

  const { consolidado } = consolidarBarragens(dams);

  return {
    dams,
    consolidado,
    isLoading: loadingDams || loadingRead || loadingInst || loadingTarps || loadingEncerradas,
    // GUPM-234 — erro propagado: sem isto uma falha de leitura (RLS/rede) deixava a tela
    // "carregada" afirmando NORMAL. O `errInst` entra na mesma corrente (GUPM-307).
    error: (errDams ?? errRead ?? errInst ?? errTarps ?? errEncerradas) as Error | null,
  };
}
